import { useState, useRef, useEffect } from 'react';
import { isValidUnit, extractUnitIdentifiers, isUnitDefined, validateUnitIdentifier } from '../utils/units';
import { BASE_UNITS } from '../utils/unitConstants';
import { useSimulationStore } from '../store/simulationStore';

interface UnitAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

export const UnitAutocomplete = ({
  value,
  onChange,
  onBlur,
  placeholder = 'e.g., kg, kg / ha, EUR / t, m^2',
  className = '',
  ariaLabel
}: UnitAutocompleteProps) => {
  const customUnits = useSimulationStore((state) => state.graph.customUnits || []);
  const addCustomUnit = useSimulationStore((state) => state.addCustomUnit);
  
  const [localValue, setLocalValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Array<{ value: string; description: string; type: 'base' | 'custom' | 'create' }>>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [validationError, setValidationError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const suggestionItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectingRef = useRef(false);

  // Sync local value with prop value when it changes externally
  useEffect(() => {
    if (value !== localValue && !isSelectingRef.current) {
      setLocalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const searchTerm = localValue.toLowerCase().trim();
    
    // Merge custom units with base units
    const customUnitSuggestions = customUnits.map(unit => ({
      value: unit,
      description: 'custom unit',
      type: 'custom' as const
    }));
    
    const allUnits = [...BASE_UNITS.map(u => ({ ...u, type: 'base' as const })), ...customUnitSuggestions];
    
    // Sort all units alphabetically by value
    const sortedAllUnits = [...allUnits].sort((a, b) => a.value.localeCompare(b.value));
    
    if (!searchTerm) {
      // Show all units when empty (sorted alphabetically)
      setFilteredSuggestions(sortedAllUnits);
      setSelectedIndex(0);
      return;
    }

    // Extract the part after the last operator for autocomplete
    // This allows autocomplete to work even after typing "kg / " or "EUR * "
    const operatorPattern = /[\s*/^]+/;
    const parts = localValue.split(operatorPattern);
    const lastPart = parts[parts.length - 1]?.trim() || '';
    const lastPartLower = lastPart.toLowerCase();
    const hasOperators = parts.length > 1;
    
    // Use the last part for filtering if it exists, otherwise use the full search term
    const filterTerm = lastPartLower || searchTerm;
    
    let filtered: Array<{ value: string; description: string; type: 'base' | 'custom' | 'create' }> = sortedAllUnits.filter(
      (unit) =>
        unit.value.toLowerCase().includes(filterTerm) ||
        unit.description.toLowerCase().includes(filterTerm)
    );
    
    // Check if we should show "Create custom unit" option
    // Only show if:
    // 1. User has typed something that doesn't match any existing unit
    // 2. The identifier is valid
    // 3. It's not already defined
    if (lastPart && !hasOperators) {
      // For standalone unit (no operators yet)
      const exactMatch = sortedAllUnits.some(u => u.value.toLowerCase() === lastPartLower);
      if (!exactMatch && !isUnitDefined(lastPart)) {
        const validation = validateUnitIdentifier(lastPart);
        if (validation.valid) {
          // Add "Create custom unit" option at the top
          filtered = [
            {
              value: lastPart,
              description: `Create custom unit "${lastPart}"`,
              type: 'create' as const
            },
            ...filtered
          ];
        }
      }
    } else if (hasOperators && lastPart) {
      // For unit after operator (e.g., "kg / users")
      const exactMatch = sortedAllUnits.some(u => u.value.toLowerCase() === lastPartLower);
      if (!exactMatch && !isUnitDefined(lastPart)) {
        const validation = validateUnitIdentifier(lastPart);
        if (validation.valid) {
          // Add "Create custom unit" option at the top
          filtered = [
            {
              value: lastPart,
              description: `Create custom unit "${lastPart}"`,
              type: 'create' as const
            },
            ...filtered
          ];
        }
      }
    }
    
    if (hasOperators && !lastPart) {
      // If just typed an operator (e.g., "kg / "), show all units (sorted alphabetically)
      setFilteredSuggestions(sortedAllUnits);
    } else {
      // Filtered results are already sorted since we filtered from sortedAllUnits
      setFilteredSuggestions(filtered);
    }
    setSelectedIndex(0);
    // Reset refs array when suggestions change
    suggestionItemRefs.current = [];
  }, [localValue, customUnits]);

  // Scroll selected item into view when selectedIndex changes (for arrow key navigation)
  useEffect(() => {
    if (showSuggestions && selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
      const item = suggestionItemRefs.current[selectedIndex];
      if (item && suggestionsRef.current) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, showSuggestions, filteredSuggestions.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setShowSuggestions(true);
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const normalizeUnit = (unitString: string): string => {
    // Normalize unit expression: remove spaces around operators for consistency
    // "kg / ha" -> "kg/ha", "m ^ 2" -> "m^2", etc.
    return unitString.trim()
      .replace(/\s*\/\s*/g, '/')
      .replace(/\s*\*\s*/g, '*')
      .replace(/\s*\^\s*/g, '^')
      .replace(/\s+/g, ' '); // Multiple spaces to single space
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Check if focus is moving to suggestions
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && suggestionsRef.current?.contains(relatedTarget)) {
      return; // Don't blur if clicking on suggestions
    }

    // Delay to allow clicking on suggestions
    blurTimeoutRef.current = setTimeout(() => {
      if (!isSelectingRef.current) {
        // Normalize and update parent with final value
        const normalized = normalizeUnit(localValue);
        setLocalValue(normalized);
        
        // Auto-add custom units if needed
        if (normalized && normalized.trim()) {
          const identifiers = extractUnitIdentifiers(normalized);
          let hasError = false;
          
          for (const identifier of identifiers) {
            // Skip if it's already defined (base or custom unit)
            if (isUnitDefined(identifier)) {
              continue;
            }
            
            // Try to add as custom unit
            const result = addCustomUnit(identifier);
            if (!result.success) {
              setValidationError(result.error);
              hasError = true;
              break;
            }
          }
          
          if (!hasError) {
            setValidationError(undefined);
          }
        } else {
          setValidationError(undefined);
        }
        
        onChange(normalized);
        setShowSuggestions(false);
        onBlur?.();
      }
    }, 200);
  };

  const selectUnit = (unitValue: string, isCreateAction: boolean = false) => {
    isSelectingRef.current = true;
    
    // If this is a "create" action, first create the custom unit
    if (isCreateAction) {
      const result = addCustomUnit(unitValue);
      if (!result.success) {
        setValidationError(result.error);
        isSelectingRef.current = false;
        return;
      }
      // Clear any previous validation errors
      setValidationError(undefined);
    }
    
    // Check if we're inserting after an operator
    // If the current value ends with an operator (like "kg / " or "EUR * "), 
    // insert the selected unit after it
    const trimmedValue = localValue.trim();
    const endsWithOperator = /[\s*/^]+$/.test(trimmedValue);
    
    let newValue: string;
    if (endsWithOperator) {
      // Insert the unit after the operator
      newValue = trimmedValue + unitValue;
    } else {
      // Check if there's a partial unit after the last operator
      const operatorPattern = /[\s*/^]+/;
      const parts = localValue.split(operatorPattern);
      const hasOperators = parts.length > 1;
      const lastPart = parts[parts.length - 1]?.trim() || '';
      
      if (hasOperators && lastPart) {
        // Replace the last part with the selected unit
        const beforeLastPart = localValue.substring(0, localValue.lastIndexOf(lastPart));
        newValue = beforeLastPart + unitValue;
      } else {
        // Replace the entire value
        newValue = unitValue;
      }
    }
    
    const normalized = normalizeUnit(newValue);
    setLocalValue(normalized);
    onChange(normalized);
    setShowSuggestions(false);
    
    // Clear blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    // Restore focus after a brief delay
    setTimeout(() => {
      inputRef.current?.focus();
      // Move cursor to end
      if (inputRef.current) {
        inputRef.current.setSelectionRange(normalized.length, normalized.length);
      }
      isSelectingRef.current = false;
    }, 10);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) {
      // If Escape is pressed without suggestions, still close
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredSuggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredSuggestions[selectedIndex]) {
          const suggestion = filteredSuggestions[selectedIndex];
          selectUnit(suggestion.value, suggestion.type === 'create');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Validate the current value
  const isValid = !localValue || isValidUnit(localValue);

  return (
    <div 
      className="unit-autocomplete-container"
      onWheel={(e) => {
        // If suggestions are open, prevent wheel events from reaching the graph
        if (showSuggestions) {
          e.stopPropagation();
        }
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className={`${className} ${(!isValid || validationError) ? 'input-error' : ''}`}
        value={localValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        aria-label={ariaLabel}
      />
      {validationError && (
        <span className="input-error-message">
          {validationError}
        </span>
      )}
      {!isValid && localValue && !validationError && (
        <span className="input-error-message">
          Invalid unit
        </span>
      )}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="unit-autocomplete-suggestions"
          onMouseDown={(e) => {
            // Prevent input blur when clicking on suggestions
            e.preventDefault();
            e.stopPropagation();
          }}
          onWheel={(e) => {
            // Prevent wheel events from propagating to the graph when scrolling the dropdown
            const target = e.currentTarget;
            const canScroll = target.scrollHeight > target.clientHeight;
            
            if (canScroll) {
              // Check if we're at the boundaries
              const isAtTop = target.scrollTop <= 0 && e.deltaY < 0;
              const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1 && e.deltaY > 0;
              
              // If we can scroll and we're not at a boundary, consume the event
              if (!isAtTop && !isAtBottom) {
                e.stopPropagation();
              }
            } else {
              // If dropdown doesn't scroll, always prevent graph scrolling
              e.stopPropagation();
            }
          }}
          onTouchMove={(e) => {
            // Prevent touch scroll events from propagating
            e.stopPropagation();
          }}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.value}-${index}`}
              ref={(el) => {
                suggestionItemRefs.current[index] = el;
              }}
              className={`unit-suggestion ${index === selectedIndex ? 'selected' : ''} unit-suggestion-${suggestion.type}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selectUnit(suggestion.value, suggestion.type === 'create');
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {suggestion.type === 'create' ? (
                <div className="unit-suggestion-create" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="unit-suggestion-create-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontFamily: 'Monaco, Courier New, monospace', color: 'var(--color-text-primary)', fontSize: '0.875rem' }}>
                      {suggestion.value}
                    </div>
                    <div className="unit-suggestion-create-description" style={{ fontSize: '0.75rem', marginTop: '1px' }}>
                      {suggestion.description}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, fontFamily: 'Monaco, Courier New, monospace', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem' }}>
                    {suggestion.value}
                    {suggestion.type === 'custom' && (
                      <span style={{ 
                        fontSize: '0.65rem', 
                        padding: '1px 4px', 
                        borderRadius: '3px', 
                        backgroundColor: 'var(--color-accent, #6366f1)', 
                        color: 'white',
                        fontWeight: 500
                      }}>
                        custom
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: index === selectedIndex ? 0.9 : 0.7, marginTop: '1px' }}>
                    {suggestion.description}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
